import React, { useContext, useEffect, useRef, useState } from "react";
//import { renderCount } from '../../../../appHelpers/appLoggers';
import {
  Form,
  Modal,
  SelectPicker,
  Divider,
  Message,
  useToaster,
  Schema,
  TagPicker,
  CheckPicker,
  Checkbox,
  Loader,
  Badge,
  Row,
  Col,
} from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import { UserContext } from "../../../_context/UserContext";
import styled from "styled-components";
import Button from "../../../../../ui/Button/Button";

export const InviteUser = ({ customerData, getUserList }) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { userRoles, userSiteGroups } = useContext(UserContext);
  const toaster = useToaster();

  // User Profile Template
  const initFormSite = {
    email_address: null, // REQUIRED
    first_name: null, // REQUIRED
    last_name: null, // REQUIRED
    phone_number: null, // OPTIONAL
    user_roles: ["basic"], // REQUIRED
    customer_id: customerData.id, // AUTOSCOPE ID
    system_list_config: [], // REQUIRED
  };

  // FORM VALIDATION
  const formRef = useRef();
  const [formError, setFormError] = useState({});
  const existRule = Schema.Types.StringType().isRequired(
    "This field is required.",
  );
  const emailRule = Schema.Types.StringType()
    .isEmail("Please enter a valid email address.")
    .isRequired("This field is required.");
  const phoneRule = Schema.Types.StringType()
    .containsNumber("Must contain only numbers")
    .maxLength(10, "Phone Number Must Be Exactly 10 Characters.")
    .minLength(10, "Phone Number Must Be Exactly 10 Characters.");

  // Handle Modal Open/Close
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    console.log("Closing modal and resetting form state");
    // Reset all form state to initial values
    setFormValue(initFormSite);
    setSelectedSiteGroups([]);
    setSelectedIndividualSites(["none"]);
    setSiteDisplayMode("individual");
    setInviterProfile(null);
    setCustomerSiteGroups([]);
    setAvailableSites([]);
    setLoading(false);
    setSiteGroupSiteIds(new Set());
    setOpen(false);
  };
  const [open, setOpen] = useState(false);

  // INITIALIZE FORM
  const [formValue, setFormValue] = useState(initFormSite);
  const roles = ["basic", "site-admin", "admin"].map((item) => ({
    label: item,
    value: item,
  }));

  // STATE FOR INVITER'S PERMISSIONS AND SITE DATA
  const [inviterProfile, setInviterProfile] = useState(null);
  const [customerSiteGroups, setCustomerSiteGroups] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [siteDisplayMode, setSiteDisplayMode] = useState("individual"); // 'individual' or 'grouped'
  const [selectedSiteGroups, setSelectedSiteGroups] = useState([]);
  const [selectedIndividualSites, setSelectedIndividualSites] = useState([
    "none",
  ]);
  const [loading, setLoading] = useState(false);
  const [siteGroupSiteIds, setSiteGroupSiteIds] = useState(new Set()); // Sites that belong to site groups

  // INITALIZE SITES FOR ACCESS (original format for backward compatibility)
  const sites = [
    { label: "Default Access", value: "none" }, // Add 'None' as first option with new label
    ...new Map(
      customerData.sites.map((item) => [
        item[0],
        {
          label: item[1],
          value: item[0],
        },
      ]),
    ).values(),
  ];

  // SUCCESS MESSAGE
  const message = (
    <Message showIcon type="success" header="Success">
      User Has Been Successfully Invited
    </Message>
  );

  // LOAD INVITER'S PROFILE AND PERMISSIONS
  const loadInviterProfile = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const profileResponse = await axiosInstance({
        url: `/user/profile`,
        headers: { authorization: `Bearer ${token}` },
      });

      setInviterProfile(profileResponse.data[0][0]);

      // Load customer site groups if needed
      const siteGroupsResponse = await axiosInstance({
        url: `/customer/site-groups/${customerData.id}`,
        headers: { authorization: `Bearer ${token}` },
      });

      const siteGroups = siteGroupsResponse.data[0] || [];
      setCustomerSiteGroups(siteGroups);

      // Collect all site IDs that belong to any site group
      const groupSiteIds = new Set();
      siteGroups.forEach((group) => {
        if (group.sites) {
          group.sites.forEach((site) => {
            groupSiteIds.add(site.id);
          });
        }
      });
      setSiteGroupSiteIds(groupSiteIds);

      // Determine the appropriate display mode based on inviter's role and permissions
      determineSiteDisplayMode(profileResponse.data[0][0], siteGroups);
    } catch (error) {
      console.error("Error loading inviter profile:", error);
    }
  };

  // DETERMINE SITE DISPLAY MODE BASED ON INVITER'S ROLE
  const determineSiteDisplayMode = (profile, siteGroups) => {
    const roles = profile.roles?.roles || [];

    if (roles.includes("superuser")) {
      // Superuser has access to everything
      setSiteDisplayMode("individual"); // Start with individual, but allow toggle
      // For superuser, show all sites including those in groups for individual selection
      setAvailableSites(sites);
    } else if (roles.includes("admin")) {
      // Customer admin - check if customer has site groups
      if (siteGroups.length > 0) {
        setSiteDisplayMode("grouped");
        // For admin in grouped mode, individual sites should exclude those in groups
        const individualSites = sites.filter(
          (site) => !siteGroupSiteIds.has(site.value),
        );
        setAvailableSites(individualSites);
      } else {
        setSiteDisplayMode("individual");
        setAvailableSites(sites);
      }
    } else {
      // Site group admin - only show sites within their site groups
      const userSiteGroupIds = userSiteGroups.map((sg) => sg.id);
      const accessibleSiteGroups = siteGroups.filter((sg) =>
        userSiteGroupIds.includes(sg.id),
      );
      const accessibleSiteIds = [];

      accessibleSiteGroups.forEach((sg) => {
        if (sg.sites) {
          accessibleSiteIds.push(...sg.sites.map((site) => site.id));
        }
      });

      // Filter out sites that are in site groups when showing individual sites
      const filteredSites = sites.filter(
        (site) =>
          accessibleSiteIds.includes(site.value) &&
          !siteGroupSiteIds.has(site.value),
      );
      setAvailableSites(filteredSites);
      setSiteDisplayMode("individual");
    }
  };

  // HANDLE SITE GROUP SELECTION WITH CHECK-ALL FUNCTIONALITY
  const handleSiteGroupChange = (groupId, checked) => {
    if (checked) {
      setSelectedSiteGroups([...selectedSiteGroups, groupId]);
      // When selecting site groups, clear "none" selection
      const currentIndividual = selectedIndividualSites.filter(
        (id) => id !== "none",
      );
      setSelectedIndividualSites(currentIndividual);
    } else {
      const updatedSiteGroups = selectedSiteGroups.filter(
        (id) => id !== groupId,
      );
      setSelectedSiteGroups(updatedSiteGroups);

      // If no site groups selected, default back to "none"
      if (
        updatedSiteGroups.length === 0 &&
        selectedIndividualSites.length === 0
      ) {
        setSelectedIndividualSites(["none"]);
      }
    }
  };

  // HANDLE INDIVIDUAL SITE SELECTION
  const handleIndividualSiteChange = (siteIds) => {
    console.log("handleIndividualSiteChange called with:", siteIds);

    const hasNone = siteIds.includes("none");
    const actualSites = siteIds.filter((id) => id !== "none");
    const hasActualSites = actualSites.length > 0;

    if (hasNone && hasActualSites) {
      // If both "none" and actual sites are selected, prioritize actual sites (remove "none")
      console.log("Removing 'none' because actual sites are selected");
      setSelectedIndividualSites(actualSites);
      updateSiteGroupSelections(actualSites);
    } else if (hasActualSites && !hasNone) {
      // If only actual sites are selected (this should handle single site selection)
      console.log("Setting actual sites:", actualSites);
      setSelectedIndividualSites(actualSites);
      updateSiteGroupSelections(actualSites);
    } else if (hasNone && !hasActualSites) {
      // If only "none" is selected
      console.log("Setting to 'none' only");
      setSelectedIndividualSites(["none"]);
      setSelectedSiteGroups([]);
    } else if (siteIds.length === 0) {
      // If nothing is selected, default to "none"
      console.log("Nothing selected, defaulting to 'none'");
      setSelectedIndividualSites(["none"]);
      setSelectedSiteGroups([]);
    } else {
      // Fallback
      console.log("Fallback case:", siteIds);
      setSelectedIndividualSites(siteIds);
    }
  };

  // UPDATE SITE GROUP SELECTIONS BASED ON INDIVIDUAL SITES
  const updateSiteGroupSelections = (siteIds) => {
    const newSelectedSiteGroups = [];
    customerSiteGroups.forEach((group) => {
      if (group.sites) {
        const groupSiteIds = group.sites.map((site) => site.id);
        const hasAllSites = groupSiteIds.every((siteId) =>
          siteIds.includes(siteId),
        );
        if (hasAllSites && groupSiteIds.length > 0) {
          newSelectedSiteGroups.push(group.id);
        }
      }
    });
    setSelectedSiteGroups(newSelectedSiteGroups);
  };

  // HELPER FUNCTION TO RESOLVE SITE GROUPS TO INDIVIDUAL SITE IDS (same as AccessListFC)
  const resolveSiteGroupsToSites = async (customerId, siteGroupIds) => {
    console.log(
      `Resolving site groups for customer ${customerId}:`,
      siteGroupIds,
    );
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      // Get site group mappings for this customer
      const response = await axiosInstance({
        url: `/customer/site-group-mappings/${customerId}`,
        headers: { authorization: `Bearer ${token}` },
      });

      const siteGroupMappings = response.data[0] || response.data || [];
      console.log("Site group mappings:", siteGroupMappings);

      // Get sites that belong to the specified site groups
      let siteIds = [];
      siteGroupIds.forEach((groupId) => {
        const siteIdsInGroup = siteGroupMappings
          .filter((mapping) => mapping.site_group_id === groupId)
          .map((mapping) => mapping.site_id);
        siteIds.push(...siteIdsInGroup);
        console.log(`Group ${groupId} contains sites:`, siteIdsInGroup);
      });

      // Remove duplicates
      const uniqueSiteIds = [...new Set(siteIds)];
      console.log("Resolved site IDs:", uniqueSiteIds);
      return uniqueSiteIds;
    } catch (error) {
      console.error(
        `Error resolving site groups for customer ${customerId}:`,
        error,
      );
      return [];
    }
  };

  // PREPARE FINAL SITE CONFIGURATION FOR SUBMISSION (following AccessListFC pattern)
  const prepareSiteConfiguration = async () => {
    // If "none" is selected, return empty array
    if (
      selectedIndividualSites.includes("none") &&
      selectedSiteGroups.length === 0
    ) {
      return [];
    }

    let finalSiteConfig = [];

    // Add individual sites (excluding "none")
    if (
      selectedIndividualSites.length > 0 &&
      !selectedIndividualSites.includes("none")
    ) {
      finalSiteConfig.push(...selectedIndividualSites);
    }

    // Resolve site groups to individual site IDs and add them (same as AccessListFC)
    if (selectedSiteGroups.length > 0) {
      const siteGroupSites = await resolveSiteGroupsToSites(
        customerData.id,
        selectedSiteGroups,
      );
      finalSiteConfig.push(...siteGroupSites);
    }

    // Remove duplicates from combined site list
    return [...new Set(finalSiteConfig)];
  };

  const onSubmit = async () => {
    // CHECK IF USER FORM IS CORRECT
    if (!formRef.current.check()) {
      console.error("Form Error");
      return;
    }

    try {
      setLoading(true);
      console.log("Getting Auth0 token...");
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });
      console.log("Auth0 token obtained successfully");

      // Prepare the final site configuration by resolving site groups (same as AccessListFC)
      console.log("Preparing site configuration...");
      const finalSiteConfig = await prepareSiteConfiguration();

      // Create unified data model similar to AccessListFC
      let userDataConfig = {
        customers__id: customerData.id,
      };

      // Add site configuration if any sites are selected
      if (finalSiteConfig.length > 0) {
        userDataConfig.and = { sites__id: finalSiteConfig };
      }

      const updatedFormValue = {
        ...formValue,
        system_list_config: finalSiteConfig, // For backward compatibility
        // Use the same structure as AccessListFC for consistency
        userModel: [userDataConfig],
        userSiteGroupsByCustomer: {
          [customerData.id]: selectedSiteGroups,
        },
        created_by: user?.email,
        last_updated_by: user?.email,
      };

      console.log("InviteUser submission data:", {
        finalSiteConfig,
        userDataConfig,
        selectedSiteGroups,
        selectedIndividualSites,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/auth/user/invite-user`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          user_form: updatedFormValue,
          created_by: user?.email,
          last_updated_by: user?.email,
        }),
      });

      toaster.push(message, "topCenter");
      getUserList();
      handleClose();
    } catch (error) {
      console.error("Error during user invitation:", error);
      console.error("Error response:", error.response);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);

      let errorMessage = "An error occurred while inviting the user";
      if (error.response?.status === 403) {
        errorMessage =
          "Permission denied. Please try refreshing the page and try again.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message.toString();
      }

      toaster.push(
        <Message showIcon type="error" header="Error">
          {errorMessage}
        </Message>,
        "topCenter",
      );

      // Don't close modal on error, allow user to try again
    } finally {
      setLoading(false);
    }
  };

  // Load inviter profile when modal opens
  useEffect(() => {
    if (open) {
      loadInviterProfile();
    }
  }, [open]);

  return (
    <>
      <Button variant="primary" size="small" onClick={handleOpen}>
        <PlusIcon style={{ marginRight: "6px", fontSize: "14px" }} />
        Invite New User
      </Button>
      <StyledModal
        size="lg"
        open={open}
        onClose={handleClose}
        backdrop="static"
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>
            Invite New User to: <b>{customerData.name}</b>
          </Modal.Title>
        </Modal.Header>
        <Divider />
        <Modal.Body>
          <Form
            fluid
            ref={formRef}
            onCheck={setFormError}
            formValue={formValue}
            onChange={(formValue) => setFormValue(formValue)}
          >
            <Row gutter={16}>
              <Col xs={12}>
                <Form.Group controlId="first_name">
                  <Form.ControlLabel style={{ display: "inline-block" }}>
                    First Name
                  </Form.ControlLabel>
                  <Form.HelpText tooltip>
                    User's First Name is Required
                  </Form.HelpText>
                  <Form.Control name="first_name" rule={existRule} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="last_name">
                  <Form.ControlLabel style={{ display: "inline-block" }}>
                    Last Name
                  </Form.ControlLabel>
                  <Form.HelpText tooltip>
                    User's Last Name is Required
                  </Form.HelpText>
                  <Form.Control name="last_name" rule={existRule} />
                </Form.Group>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={12}>
                <Form.Group controlId="email_address">
                  <Form.ControlLabel style={{ display: "inline-block" }}>
                    Email Address
                  </Form.ControlLabel>
                  <Form.HelpText tooltip>
                    User's Email Address is Required
                  </Form.HelpText>
                  <Form.Control name="email_address" rule={emailRule} />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="phone_number">
                  <Form.ControlLabel style={{ display: "inline-block" }}>
                    Phone Number
                  </Form.ControlLabel>
                  <Form.HelpText tooltip>
                    Phone Number Should Contain Only 10 Digits and No Dashes
                  </Form.HelpText>
                  <Form.Control name="phone_number" rule={phoneRule} />
                </Form.Group>
              </Col>
            </Row>
            <RolePickerContainer>
              <Form.ControlLabel>Roles</Form.ControlLabel>
              <StyledSelectPicker
                searchable={false}
                name="user_roles"
                data={roles}
                value={formValue.user_roles[0]} // SelectPicker takes single value
                onChange={
                  (value) => setFormValue({ ...formValue, user_roles: [value] }) // Wrap in array for compatibility
                }
                placeholder="Select user role"
                container={() => document.body}
                preventOverflow
                cleanable={false}
              />
            </RolePickerContainer>
            <Form.Group controlId="system_list_config">
              <Form.ControlLabel>Site Access</Form.ControlLabel>
              {loading ? (
                <LoaderContainer>
                  <Loader size="md" content="Loading site permissions..." />
                </LoaderContainer>
              ) : (
                <SiteSelectionContainer>
                  {/* Mode Toggle for Superuser */}
                  {inviterProfile?.roles?.roles?.includes("superuser") &&
                    customerSiteGroups.length > 0 && (
                      <ModeToggleContainer>
                        <ModeLabel>Site Selection Mode:</ModeLabel>
                        <PillButtonGroup>
                          <PillButton
                            active={siteDisplayMode === "individual"}
                            onClick={() => setSiteDisplayMode("individual")}
                          >
                            Individual Sites
                          </PillButton>
                          <PillButton
                            active={siteDisplayMode === "grouped"}
                            onClick={() => setSiteDisplayMode("grouped")}
                          >
                            Site Groups
                          </PillButton>
                        </PillButtonGroup>
                      </ModeToggleContainer>
                    )}

                  {/* Individual Sites Selection */}
                  {siteDisplayMode === "individual" && (
                    <SitePickerContainer>
                      <SitePickerLabel>
                        Select Individual Sites
                        {availableSites.length > 0 && (
                          <Badge
                            content={availableSites.length}
                            style={{ marginLeft: "8px" }}
                          />
                        )}
                      </SitePickerLabel>
                      <StyledCheckPicker
                        placeholder="Choose sites (default: None)"
                        data={availableSites}
                        value={selectedIndividualSites}
                        onChange={handleIndividualSiteChange}
                        block
                        size="lg"
                        menuMaxHeight={250}
                        searchable
                        cleanable={false}
                        placement="auto"
                        preventOverflow
                        renderExtraFooter={() => (
                          <div
                            style={{
                              padding: "10px 12px",
                              borderTop: "1px solid #e5e5e5",
                            }}
                          >
                            <Checkbox
                              checked={
                                selectedIndividualSites.length > 1 ||
                                (selectedIndividualSites.length === 1 &&
                                  !selectedIndividualSites.includes("none"))
                              }
                              onChange={(_, checked) => {
                                if (checked) {
                                  // Select all sites except "none"
                                  const allSiteIds = availableSites
                                    .filter((site) => site.value !== "none")
                                    .map((site) => site.value);
                                  setSelectedIndividualSites(allSiteIds);
                                  updateSiteGroupSelections(allSiteIds);
                                } else {
                                  // Unselect all and go back to "none"
                                  setSelectedIndividualSites(["none"]);
                                  setSelectedSiteGroups([]);
                                }
                              }}
                            >
                              All Sites
                            </Checkbox>
                          </div>
                        )}
                      />
                    </SitePickerContainer>
                  )}

                  {/* Site Groups Selection */}
                  {siteDisplayMode === "grouped" &&
                    customerSiteGroups.length > 0 && (
                      <SitePickerContainer>
                        <SitePickerLabel>
                          Select Site Groups
                          <Badge
                            content={customerSiteGroups.length}
                            style={{ marginLeft: "8px" }}
                          />
                        </SitePickerLabel>
                        <StyledCheckPicker
                          placeholder="Choose site groups"
                          data={customerSiteGroups.map((group) => ({
                            label: group.name,
                            value: group.id,
                          }))}
                          value={selectedSiteGroups}
                          onChange={(values) => {
                            setSelectedSiteGroups(values);

                            // When selecting site groups, clear "none" from individual sites
                            if (values.length > 0) {
                              const currentIndividual =
                                selectedIndividualSites.filter(
                                  (id) => id !== "none",
                                );
                              setSelectedIndividualSites(currentIndividual);
                            } else {
                              // If no site groups selected, default back to "none"
                              setSelectedIndividualSites(["none"]);
                            }
                          }}
                          size="lg"
                          menuMaxHeight={250}
                          searchable
                          cleanable={false}
                          placement="auto"
                          preventOverflow
                          block
                        />

                        {/* Individual Site Override - Only shows sites from selected groups */}
                        {/*selectedSiteGroups.length > 0 && (
                          <div style={{ marginTop: "16px" }}>
                            <SitePickerLabel
                              style={{ fontSize: "14px", color: "#878787" }}
                            >
                              Fine-tune selection (uncheck sites to exclude from
                              selected groups):
                            </SitePickerLabel>
                            <StyledCheckPicker
                              placeholder="All sites from selected groups are included (uncheck to exclude)"
                              data={customerSiteGroups
                                .filter((group) =>
                                  selectedSiteGroups.includes(group.id),
                                )
                                .flatMap((group) => {
                                  if (!group.sites || group.sites.length === 0)
                                    return [];
                                  return group.sites.map((site) => ({
                                    label: `${site.name} (${group.name})`,
                                    value: site.id,
                                  }));
                                })}
                              value={selectedIndividualSites.filter((siteId) =>
                                customerSiteGroups
                                  .filter((group) =>
                                    selectedSiteGroups.includes(group.id),
                                  )
                                  .some(
                                    (group) =>
                                      group.sites &&
                                      group.sites.some(
                                        (site) => site.id === siteId,
                                      ),
                                  ),
                              )}
                              onChange={(values) => {
                                // Get all sites from selected groups
                                const allGroupSites = customerSiteGroups
                                  .filter((group) =>
                                    selectedSiteGroups.includes(group.id),
                                  )
                                  .flatMap((group) =>
                                    group.sites
                                      ? group.sites.map((site) => site.id)
                                      : [],
                                  );

                                // Keep non-group sites and add selected group sites
                                const nonGroupSites =
                                  selectedIndividualSites.filter(
                                    (siteId) => !allGroupSites.includes(siteId),
                                  );

                                setSelectedIndividualSites([
                                  ...nonGroupSites,
                                  ...values,
                                ]);
                              }}
                              size="md"
                              menuMaxHeight={200}
                              searchable
                              cleanable
                              placement="auto"
                              preventOverflow
                              block
                            />
                          </div>
                        )*/}
                      </SitePickerContainer>
                    )}

                  {/* Selection Summary */}
                  <SelectionSummary>
                    {selectedSiteGroups.length > 0 && (
                      <SummaryItem>
                        <SummaryLabel>Site Groups:</SummaryLabel>
                        <SummaryBadge
                          content={selectedSiteGroups.length}
                          color="green"
                        />
                      </SummaryItem>
                    )}
                    {selectedIndividualSites.length > 0 &&
                      !selectedIndividualSites.includes("none") && (
                        <SummaryItem>
                          <SummaryLabel>Individual Sites:</SummaryLabel>
                          <SummaryBadge
                            content={selectedIndividualSites.length}
                            color="blue"
                          />
                        </SummaryItem>
                      )}
                    {selectedIndividualSites.includes("none") &&
                      selectedSiteGroups.length === 0 && (
                        <DefaultScopeNote>
                          💡 No specific selection - user will your your access
                          scope
                        </DefaultScopeNote>
                      )}
                    {selectedSiteGroups.length === 0 &&
                      selectedIndividualSites.length === 0 && (
                        <DefaultScopeNote>
                          💡 No specific selection - user will inherit your
                          access scope
                        </DefaultScopeNote>
                      )}
                  </SelectionSummary>
                </SiteSelectionContainer>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>

        <StyledModalFooter>
          <Button onClick={handleClose} variant="subtle" disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={loading}>
            {loading ? "Inviting..." : "Submit"}
          </Button>
        </StyledModalFooter>
      </StyledModal>
    </>
  );
};

const errMessage = (
  <Message showIcon type="error" header="Error">
    Something went wrong. Please try again.
  </Message>
);

// Styled Components
const StyledModal = styled(Modal)`
  .rs-modal-dialog {
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }

  .rs-modal-header {
    background: linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%);
    color: white;
    border-radius: 24px 24px 0 0;
    padding: 20px 32px;

    .rs-modal-title {
      color: white;
      font-weight: 600;
      font-size: 18px;
    }

    .rs-modal-header-close {
      color: white;
      opacity: 0.8;
      font-size: 20px;

      &:hover {
        opacity: 1;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 50%;
      }
    }
  }

  .rs-modal-body {
    padding: 24px;
    padding-top: 0px;
    max-height: 70vh;
    overflow-y: auto;
  }

  .rs-form-control-wrapper {
    .rs-input {
      border-radius: 20px;
      border: 2px solid #e9ecef;
      padding: 12px 20px;
      transition: all 0.3s ease;
      min-height: 48px !important;
      height: 48px;
      box-sizing: border-box;

      &:hover {
        border-color: #2c5aa0;
        box-shadow: 0 2px 8px rgba(44, 90, 160, 0.1);
      }

      &:focus {
        border-color: #2c5aa0;
        box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1);
      }
    }
  }

  .rs-form-group {
    margin-bottom: 4px;

    .rs-form-control-label {
      margin-bottom: 6px;
      display: block;
      font-weight: 500;
      font-size: 14px;
    }

    .rs-help-block {
      margin-bottom: 6px;
      font-size: 12px;
    }
  }

  .rs-picker-toggle {
    border-radius: 20px;
    border: 2px solid #e9ecef;
    padding: 12px 20px;
    transition: all 0.3s ease;
    min-height: 48px !important;
    height: 48px;
    display: flex;
    align-items: center;
    box-sizing: border-box;

    &:hover {
      border-color: #2c5aa0;
      box-shadow: 0 2px 8px rgba(44, 90, 160, 0.1);
    }

    &:focus {
      border-color: #2c5aa0;
      box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1);
    }
  }

  .rs-picker-menu {
    border-radius: 16px;
    border: none;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    z-index: 9999;
    max-height: 300px;
  }
`;

const StyledModalFooter = styled(Modal.Footer)`
  padding: 24px 32px;
  background: #f8f9fa;
  border-radius: 0 0 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  .rs-btn {
    border-radius: 25px;
    padding: 12px 32px;
    font-weight: 500;
    transition: all 0.3s ease;

    &.rs-btn-primary {
      background: linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%);
      border: none;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 16px rgba(44, 90, 160, 0.3);
      }
    }

    &.rs-btn-subtle {
      border: 2px solid #ff0800;

      &:hover {
        border-color: #ff0800;
        color: #ff0800;
        transform: translateY(-1px);
      }
    }
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #f8f9fb 0%, #e1e8ed 100%);
  border-radius: 16px;
  margin: 16px 0;
`;

const SiteSelectionContainer = styled.div`
  margin: 16px 0;
`;

const ModeToggleContainer = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #2c5aa0 0%, #4a7bc8 100%);
  border-radius: 16px;
  color: white;
`;

const ModeLabel = styled.div`
  font-weight: 600;
  margin-bottom: 12px;
  color: white;
`;

const PillButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const PillButton = styled.button`
  padding: 10px 20px;
  border-radius: 25px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: ${(props) =>
    props.active ? "rgba(255, 255, 255, 0.9)" : "transparent"};
  color: ${(props) => (props.active ? "#333" : "white")};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(
      255,
      255,
      255,
      ${(props) => (props.active ? "0.9" : "0.2")}
    );
    transform: translateY(-1px);
  }
`;

const SitePickerContainer = styled.div`
  margin-bottom: 16px;
`;

const SitePickerLabel = styled.div`
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
  display: flex;
  align-items: center;
`;

const StyledCheckPicker = styled(CheckPicker)`
  .rs-picker-toggle {
    border-radius: 20px;
    border: 2px solid #e9ecef;
    padding: 7px 12px;
    background: white;
    transition: all 0.3s ease;

    &:hover {
      border-color: #2c5aa0;
      box-shadow: 0 4px 12px rgba(44, 90, 160, 0.1);
    }

    &:focus {
      border-color: #2c5aa0;
      box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1);
    }
  }

  .rs-picker-menu {
    border-radius: 16px;
    border: none;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    z-index: 9999;
  }

  .rs-picker-menu-item {
    padding: 12px 16px;
    transition: all 0.2s ease;

    &:hover {
      background: linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%);
      color: white;
    }
  }
`;

const SelectionSummary = styled.div`
  background: linear-gradient(135deg, #e8f4f8 0%, #d1e7dd 100%);
  padding: 12px 16px;
  border-radius: 16px;
  margin-top: 16px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SummaryLabel = styled.span`
  color: #333;
  font-weight: 500;
  margin-right: 8px;
`;

const SummaryBadge = styled(Badge)`
  border-radius: 12px;
`;

const DefaultScopeNote = styled.div`
  color: #333;
  font-weight: 500;
  text-align: center;
  font-style: italic;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 24px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const RolePickerContainer = styled(Form.Group)`
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;

  .rs-form-control-label {
    margin-bottom: 6px;
    display: block;
    font-weight: 500;
    font-size: 14px;
    text-align: center;
  }
`;

const StyledTagPicker = styled(TagPicker)`
  /* Completely override the picker container */
  &.rs-picker {
    position: relative;
    width: 180px; /* Make it a bit wider */
    max-width: 180px;
  }

  /* Hide any default styling completely */
  &.rs-picker,
  &.rs-picker-default,
  &.rs-picker-tag {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    outline: none !important;
  }

  /* Style the actual toggle button */
  .rs-picker-toggle {
    /* Reset all inherited styles */
    border: 2px solid #e9ecef !important;
    border-radius: 20px !important;
    padding: 0 12px !important;
    background: white !important;
    min-height: 38px !important;
    transition: all 0.3s ease !important;
    height: 38px !important;
    display: flex !important;
    align-items: center !important;
    display: flex !important;
    align-items: bottom !important;
    box-sizing: border-box !important;
    cursor: pointer !important;
    outline: none !important;
    box-shadow: none !important;

    /* Remove any default button styling */
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &:hover {
      border-color: #2c5aa0 !important;
      box-shadow: 0 2px 8px rgba(44, 90, 160, 0.1) !important;
      background: white !important;
    }

    &:focus,
    &.rs-picker-focused,
    &.rs-picker-toggle-active {
      border-color: #2c5aa0 !important;
      box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1) !important;
      background: white !important;
      outline: none !important;
    }

    /* Override any focus states that might show the old styling */
    &:focus-visible,
    &:focus-within {
      border-color: #2c5aa0 !important;
      box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1) !important;
      background: white !important;
      outline: none !important;
    }
  }

  /* Style the content area to match CheckPicker behavior */
  .rs-picker-toggle-value {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: calc(100% - 20px) !important;
    height: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1 !important;
    text-align: center !important;
    position: relative !important;
    z-index: 1 !important;
    overflow: hidden !important;
  }

  /* Style the value list container */
  .rs-picker-toggle-value .rs-picker-value-list {
    display: inline !important;
    width: 100% !important;
    text-align: center !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .rs-picker-toggle-placeholder {
    color: #999 !important;
    font-size: 14px !important;
    text-align: left !important;
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    line-height: 1.4 !important;
  }

  .rs-picker-toggle-caret {
    color: #878787 !important;
    margin-left: auto !important;
  }

  /* Style the dropdown menu */
  .rs-picker-menu {
    border-radius: 16px !important;
    border: none !important;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
    overflow: hidden !important;
    z-index: 9999 !important;
    max-height: 200px !important;
  }

  .rs-picker-menu-item {
    padding: 10px 16px !important;
    transition: all 0.2s ease !important;

    &:hover {
      background: linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%) !important;
      color: white !important;
    }
  }

  /* Style the selected tags to match CheckPicker display */
  .rs-picker-tag,
  .rs-tag,
  .rs-picker-value-item {
    background: transparent !important;
    color: #333 !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    font-size: 14px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 38px !important;
    line-height: 1 !important;
    text-align: center !important;
    width: 100% !important;
    position: relative !important;
    z-index: 2 !important;
  }

  .rs-picker-tag .rs-picker-tag-text,
  .rs-tag .rs-tag-text,
  .rs-picker-value-item-text {
    color: #333 !important;
    font-weight: normal !important;
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 100% !important;
    line-height: 1 !important;
  }

  .rs-picker-toggle-clean,
  .rs-picker-tag-remove,
  .rs-picker-tag-close,
  .rs-tag-close,
  .rs-picker-value-item-close {
    display: none !important;
  }

  /* Override any close button that might appear */
  [class*="tag-remove"],
  [class*="tag-close"],
  [class*="close-button"],
  [class*="-clean"],
  [class*="-clear"],
  [class*="remove"],
  .rs-icon-close {
    display: none !important;
  }

  .rs-picker-tag-wrapper {
    display: inline !important;
  }

  /* When multiple items, add comma separation */
  .rs-picker-tag:not(:last-child)::after,
  .rs-tag:not(:last-child)::after,
  .rs-picker-value-item:not(:last-child)::after {
    content: ", ";
    color: #333 !important;
    margin-left: 0 !important;
  }
`;

const StyledSelectPicker = styled(SelectPicker)`
  width: 180px;
  max-width: 180px;

  .rs-picker-toggle {
    border-radius: 20px !important;
    border: 2px solid #e9ecef !important;
    padding: 12px 20px !important;
    background: white !important;
    transition: all 0.3s ease !important;
    min-height: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center !important;
    box-sizing: border-box !important;

    &:hover {
      border-color: #2c5aa0 !important;
      box-shadow: 0 2px 8px rgba(44, 90, 160, 0.1) !important;
    }

    &:focus {
      border-color: #2c5aa0 !important;
      box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1) !important;
    }
  }

  .rs-picker-toggle-caret {
    color: #878787 !important;
    display: flex !important;
    align-items: center !important;
    height: 70% !important;
    margin-left: auto !important;
  }

  .rs-picker-menu {
    border-radius: 16px !important;
    border: none !important;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
    overflow: hidden !important;
    z-index: 9999 !important;
  }

  .rs-picker-menu-item {
    padding: 10px 16px !important;
    transition: all 0.2s ease !important;

    &:hover {
      background: linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%) !important;
      color: white !important;
    }
  }
`;
